import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";
import AddBranchMarker from "components/AddBranchMarker";
import BranchFormDialog from "components/AddBranchDialog";
import BranchModel from "models/branchModel";
import { addBranch } from "services/api";
import { Alert, Box, Button, CircularProgress, Snackbar, Stack, TextField } from "@mui/material";
import { updateCompanyDetails, getAreas } from "services/api";
import { deleteBranch } from "services/api";
import MDTypography from "components/MDTypography";
import { updateBranchJobs, updateBranch } from "services/api";
import { getJobToBranch } from "services/api";
import * as XLSX from "xlsx";
import MDButton from "components/MDButton";
import { CloudDownload, CloudUpload } from "@mui/icons-material";
import { exportData } from "services/api";
import { useNavigate } from "react-router-dom";
import { detectDataType } from "services/utils";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import { update } from "@react-spring/web";
import ImportButton from "components/ImportButton";

const BranchView = ({
  branches,
  companyId,
  updateBranches,
  jobs,
  setJobToBranch,
  jobToBranch,
  center,
  areas,
  canEdit,
}) => {
  const { t } = useTranslation();
  const snackbarRef = useRef();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [editMode, setEditMode] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [newBranch, setNewBranch] = useState({
    branchName: "",
    address: "",
    companyId: companyId,
    latitude: null,
    longitude: null,
  });

  const [search, setSearch] = useState("");

  const [confirmEditBranchOpen, setConfirmEditBranchOpen] = useState(false);
  // const [pendingEditBranch, setPendingEditBranch] = useState(null);
  const [pendingNewLocation, setPendingNewLocation] = useState(null); // temporarily stores clicked coordinates
  const [editBranchLocation, setEditBranchLocation] = useState(null);
  const editMarkerRef = useRef(null);

  const filteredBranches = branches?.filter((branch) =>
    branch.branchName.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (!dialogOpen) {
      setNewBranch({
        branchName: "",
        address: "",
        companyId: companyId,
        latitude: null,
        longitude: null,
      });
      setEditMode(false);
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (editBranchLocation && editMarkerRef.current) {
      setTimeout(() => {
        editMarkerRef.current.openPopup();
      }, 0);
    }
  }, [editBranchLocation]);

  // Function to handle adding a new branch
  const handleAddBranch = (location) => {
    if (!canEdit) return;
    setNewBranch((prevBranch) => ({
      ...prevBranch, // Spread the existing branch properties
      latitude: location.latitude, // Update latitude
      longitude: location.longitude, // Update longitude
    }));
    setDialogOpen(true); // Open the dialog
  };

  // Function to handle form submission
  const handleSubmitBranch = async (branch, jobToBranch) => {
    const branchToADD = new BranchModel(branch);

    const normalizedBranchName = branchToADD.branchName.trim().toLowerCase();

    // Check if the branch name already exists, excluding the one being updated
    const nameExists = branches.some(
      (b) =>
        b.branchName.trim().toLowerCase() === normalizedBranchName &&
        b.branchId !== branchToADD.branchId // Exclude itself if updating
    );

    if (nameExists) {
      snackbarRef.current?.showSnackbar(t("admin.branches.branchNameExists"), "error");
      return;
    }

    const res = await addBranch(branchToADD);
    if (res != "error") {
      if (editMode) {
        // Update the existing branch
        updateBranches(
          branches.map((branch) =>
            branch.branchId === branchToADD.branchId
              ? {
                  ...branch,
                  companyId: branchToADD.companyId,
                  address: branchToADD.address,
                  branchName: branchToADD.branchName,
                  areaId: branchToADD.areaId,
                }
              : branch
          )
        );
      } else {
        updateBranches([...branches, new BranchModel(res)]);
      }
      const e = await updateBranchJobs({
        branchId: editMode ? branch.branchId : res.branchId,
        jobs: jobToBranch,
      });
      if (e != "error") {
        const resu = await getJobToBranch();
        if (resu != "error") setJobToBranch(resu);
      }
    }
  };

  const handleEditBranch = async (branchtoEdit) => {
    // Check if a branch with the same name already exists (case-insensitive)

    setNewBranch(branchtoEdit);
    setEditMode(true);
    setDialogOpen(true); // Open the dialog
  };

  const handleEditBranchLocation = (location) => {
    if (!editBranchLocation) return;
    setPendingNewLocation(location);
    setConfirmEditBranchOpen(true); // open confirmation dialog
  };

  // Save the new location after confirmation
  const handleSaveBranchLocation = async () => {
    if (!editBranchLocation || !pendingNewLocation) return;

    const updatedBranch = { ...editBranchLocation, ...pendingNewLocation };
    const res = await updateBranch(updatedBranch);

    if (res) {
      updateBranches(branches.map((b) => (b.branchId === res.branchId ? res : b)));
      setEditBranchLocation(null); // exit edit mode
      setPendingNewLocation(null);
    } else {
      snackbarRef.current?.showSnackbar(t("admin.branches.updateFailed"), "error");
    }

    setConfirmEditBranchOpen(false);
  };

  const handleDelete = async (branchId) => {
    let res = await deleteBranch(branchId);
    if (res == "success") {
      const updated = branches.filter((branch) => branch.branchId !== branchId);
      updateBranches(updated);
    } else if (res[0] == "C") {
      snackbarRef.current?.showSnackbar(t("admin.branches.deleteErrorEmployees"), "error");
      return;
    } else {
      snackbarRef.current?.showSnackbar(t("admin.branches.deleteErrorGeneric"), "error");
      return;
    }
  };

  //IMPORT AND EXPORT
  const handleExport = async () => {
    setDownloadLoading(true);
    await exportData("branches");
    setDownloadLoading(false);
  };

  const customIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png", // Default Leaflet marker
    iconSize: [25, 41], // Size of the icon
    iconAnchor: [12, 41], // Positioning of the icon
    popupAnchor: [1, -34], // Positioning of the popup
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    shadowSize: [41, 41],
  });

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <MDTypography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {t("admin.branches.numberOfBranches")} {branches?.length}
        </MDTypography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
          <MDButton color="primary" onClick={handleExport} startIcon={<CloudDownload />}>
            {downloadLoading ? <CircularProgress size={20} color="inherit" /> : t("export")}
          </MDButton>
          <ImportButton
            // Only allow Jobs to be imported from this page
            allowedTypes={["branches"]}
            // Pass context needed for the ImportReview page (optional now, mostly companyId)
            extraState={{ companyId: companyId }}
            // Handle UI feedback
            onError={handleImportError}
            disabled={!canEdit}
          />
        </Stack>
      </Box>

      <TextField
        label={t("admin.branches.searchBranch")}
        variant="outlined"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      <MapContainer center={center} zoom={6} style={{ height: "75vh", width: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {editBranchLocation ? (
          <>
            {/* Show only the branch being edited */}
            <Marker
              ref={editMarkerRef}
              key={editBranchLocation.branchId}
              position={[editBranchLocation.latitude, editBranchLocation.longitude]}
              icon={customIcon}
            >
              <Popup autoClose={false} closeOnClick={false} closeButton={false}>
                <Box sx={{ width: 160, textAlign: "center", p: 1, wordWrap: "break-word" }}>
                  <h2 style={{ fontSize: "1rem", margin: "0 0 4px 0", lineHeight: 1.2 }}>
                    {editBranchLocation.branchName}
                  </h2>
                  <p style={{ fontSize: "0.85rem", margin: 0, lineHeight: 1.2 }}>
                    {t("admin.areas.click")}
                  </p>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
                    <Button
                      color="secondary"
                      onClick={() => setEditBranchLocation(null)}
                      sx={{ color: "error.main" }}
                    >
                      {t("admin.employees.delete.cancel")}
                    </Button>
                  </Box>
                </Box>
              </Popup>
            </Marker>

            <AddBranchMarker onAddBranch={handleEditBranchLocation} />
          </>
        ) : (
          filteredBranches?.map((branch) => (
            <Marker
              key={branch.branchId}
              position={[branch.latitude, branch.longitude]}
              icon={customIcon}
            >
              <Popup>
                <Box>
                  <h3 style={{ textAlign: "center", margin: 0 }}>{branch.branchName}</h3>
                  <p>{branch.address}</p>
                  <Button
                    disabled={!canEdit}
                    onClick={() => {
                      handleEditBranch(branch);
                    }}
                  >
                    {t("admin.branches.edit")}
                  </Button>

                  <Button
                    disabled={!canEdit}
                    onClick={() => {
                      setEditBranchLocation(branch);
                    }}
                  >
                    {t("admin.areas.editLoc")}
                  </Button>
                </Box>
              </Popup>
            </Marker>
          ))
        )}
        {/* <AddBranchMarker onAddBranch={handleAddBranch} /> */}
        <AddBranchMarker
          onAddBranch={editBranchLocation ? handleEditBranchLocation : handleAddBranch}
        />
      </MapContainer>

      <ConfirmationDialog
        open={confirmEditBranchOpen}
        onClose={() => setConfirmEditBranchOpen(false)}
        onConfirm={handleSaveBranchLocation}
        message={`Are you sure you want to move "${editBranchLocation?.branchName}" here?`}
      />

      {/* Dialog for adding a new branch */}
      <BranchFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmitBranch}
        branch={newBranch}
        selectedCompany={companyId}
        editMode={editMode}
        handleDelete={handleDelete}
        jobs={jobs}
        jobToBranch={jobToBranch}
        areas={areas}
      />
      {/* Snackbar for Notifications */}
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
};

BranchView.propTypes = {
  branches: PropTypes.array.isRequired,
  companyId: PropTypes.number.isRequired,
  companies: PropTypes.array.isRequired,
  updateBranches: PropTypes.func.isRequired,
  company: PropTypes.object.isRequired,
  jobs: PropTypes.array.isRequired,
  setJobToBranch: PropTypes.func.isRequired,
  jobToBranch: PropTypes.array.isRequired,
  center: PropTypes.array.isRequired,
  areas: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default BranchView;
